import { queryOptions, useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, Link } from "@tanstack/react-router"
import { format } from "date-fns"
import { ptBR } from "date-fns/locale"
import {
  ChevronLeft,
  ChevronRight,
  FileText,
  Pencil,
  Plus,
  Trash2,
  Upload,
} from "lucide-react"
import { useState } from "react"

import { ChangelogTabs } from "@/components/changelog/changelog-tabs"
import { DeleteEntryDialog } from "@/components/changelog/delete-entry-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { listEntries } from "@/http/changelog/entries"
import type { TChangelogEntryWithTags } from "@/http/changelog/types"

const entriesQueryOptions = (
  serviceId: string,
  organizationSlug: string,
  page: number,
) =>
  queryOptions({
    queryKey: ["changelog-entries", serviceId, organizationSlug, page],
    queryFn: async () => {
      const response = await listEntries({
        serviceId,
        organizationSlug,
        page,
      })
      if (response.error) throw new Error(response.error.message)
      return response.data
    },
  })

type TSearchParams = {
  page?: number
}

export const Route = createFileRoute(
  "/_authenticated/$orgSlug/changelog/services/$serviceId/entries/",
)({
  validateSearch: (search: Record<string, unknown>): TSearchParams => ({
    page: Number(search.page) || 1,
  }),
  loader: async ({ context, params }) => {
    await context.queryClient.ensureQueryData(
      entriesQueryOptions(params.serviceId, params.orgSlug, 1),
    )
    return {
      title: "Entradas",
      path: `/${params.orgSlug}/changelog/services/${params.serviceId}/entries`,
    }
  },
  component: EntriesPage,
})

function getStatusBadge(entry: TChangelogEntryWithTags) {
  if (!entry.isPublished) {
    return <Badge variant="secondary">Rascunho</Badge>
  }

  if (entry.publishedAt && new Date(entry.publishedAt) > new Date()) {
    return <Badge variant="outline">Agendado</Badge>
  }

  return <Badge>Publicado</Badge>
}

function EntriesPage() {
  const { orgSlug, serviceId } = Route.useParams()
  const { page = 1 } = Route.useSearch()
  const navigate = Route.useNavigate()

  const { data, refetch } = useSuspenseQuery(
    entriesQueryOptions(serviceId, orgSlug, page),
  )

  const entries = data?.entries ?? []
  const totalPages = data?.totalPages ?? 1

  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean
    entry: TChangelogEntryWithTags | null
  }>({ open: false, entry: null })

  const handlePageChange = (newPage: number) => {
    navigate({
      search: (prev) => ({
        ...prev,
        page: newPage,
      }),
    })
  }

  const handleDeleteSuccess = () => {
    refetch()
  }

  const routeParams = { orgSlug, serviceId }

  return (
    <div className="space-y-6">
      <ChangelogTabs
        orgSlug={orgSlug}
        serviceId={serviceId}
        activeTab="entries"
      />
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <FileText className="text-muted-foreground size-6" />
          <div>
            <h1 className="text-2xl font-bold md:text-3xl">Entradas</h1>
            <p className="text-muted-foreground">
              Gerencie as entradas do changelog
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild>
            <Link
              to="/$orgSlug/changelog/services/$serviceId/entries/import"
              params={routeParams}
            >
              <Upload className="mr-2 size-4" />
              Importar markdown
            </Link>
          </Button>
          <Button asChild>
            <Link
              to="/$orgSlug/changelog/services/$serviceId/entries/new"
              params={routeParams}
            >
              <Plus className="mr-2 size-4" />
              Nova entry
            </Link>
          </Button>
        </div>
      </div>

      {entries.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground mb-4">
              Nenhuma entry cadastrada
            </p>
            <Button asChild>
              <Link
                to="/$orgSlug/changelog/services/$serviceId/entries/new"
                params={routeParams}
              >
                <Plus className="mr-2 size-4" />
                Crie sua primeira entry
              </Link>
            </Button>
          </CardContent>
        </Card>
      )}

      {entries.length > 0 && (
        <>
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Título</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Tags</TableHead>
                    <TableHead>Data de publicação</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entries.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell className="font-medium">
                        {entry.title}
                      </TableCell>
                      <TableCell>{getStatusBadge(entry)}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {entry.tags.map((tag) => (
                            <Badge
                              key={tag.id}
                              variant="outline"
                              className="gap-1"
                            >
                              <div
                                className="size-2 rounded-full"
                                style={{
                                  backgroundColor: tag.color,
                                }}
                              />
                              {tag.name}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        {entry.publishedAt
                          ? format(
                              new Date(entry.publishedAt),
                              "dd/MM/yyyy HH:mm",
                              { locale: ptBR },
                            )
                          : "-"}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" asChild>
                            <Link
                              to="/$orgSlug/changelog/services/$serviceId/entries/$entryId"
                              params={{
                                ...routeParams,
                                entryId: entry.id,
                              }}
                            >
                              <Pencil className="size-4" />
                            </Link>
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() =>
                              setDeleteDialog({
                                open: true,
                                entry,
                              })
                            }
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <Button
                variant="outline"
                size="icon"
                disabled={page <= 1}
                onClick={() => handlePageChange(page - 1)}
              >
                <ChevronLeft className="size-4" />
              </Button>
              <span className="text-muted-foreground text-sm">
                Página {page} de {totalPages}
              </span>
              <Button
                variant="outline"
                size="icon"
                disabled={page >= totalPages}
                onClick={() => handlePageChange(page + 1)}
              >
                <ChevronRight className="size-4" />
              </Button>
            </div>
          )}
        </>
      )}

      {deleteDialog.entry && (
        <DeleteEntryDialog
          open={deleteDialog.open}
          onOpenChange={(open) =>
            setDeleteDialog({
              open,
              entry: open ? deleteDialog.entry : null,
            })
          }
          entryId={deleteDialog.entry.id}
          entryTitle={deleteDialog.entry.title}
          serviceId={serviceId}
          organizationSlug={orgSlug}
          onSuccess={handleDeleteSuccess}
        />
      )}
    </div>
  )
}
